import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Icon from './Icon';
import BrandIcon from './BrandIcon';
import Avatar from './Avatar';
import { getUserRole, USER_ROLES } from '../utils/rolePermissions';
import { useOrgBrand } from '../context/OrgBrandContext';
import { useModal } from '../context/ModalContext';
import CommentModal from './CommentModal';
import QuickActionsEditModal from './QuickActionsEditModal';
import '../styles/DashboardHome.css';

const DashboardHome = ({ user, onSectionChange, isProxyMode = false }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { brands, organizations, setBrand, setOrganization, selectedBrand, selectedOrganization } = useOrgBrand();
  const userRole = getUserRole(user);
  const modal = useModal();

  // To Do List state
  const [ideasItems, setIdeasItems] = useState([]);
  const [contentDraftItems, setContentDraftItems] = useState([]);
  const [brandVoiceItems, setBrandVoiceItems] = useState([]);
  const [todoLoading, setTodoLoading] = useState(true);

  // Revision modal state
  const [revisionModal, setRevisionModal] = useState({
    open: false,
    type: null, // 'idea'
    itemId: null,
    docId: null, // for ideas
    ideaId: null // for ideas
  });
  const [revisionComment, setRevisionComment] = useState('');

  // Comment modal state
  const [commentModalState, setCommentModalState] = useState({ open: false, itemId: null, itemType: null, docId: null, comments: [] });

  // Quick Actions Edit Modal state
  const [showQuickActionsModal, setShowQuickActionsModal] = useState(false);
  const [selectedQuickActions, setSelectedQuickActions] = useState([]);

  // Calendar state
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [calendarLoading, setCalendarLoading] = useState(true);
  const [calendarError, setCalendarError] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());

  useEffect(() => {
    setTodoLoading(true);
    Promise.all([
      fetchIdeasItems(),
      fetchContentDraftItems(),
      fetchBrandVoiceCompletions()
    ]).finally(() => setTodoLoading(false));
    fetchCalendarEvents();
    fetchQuickActionsPreferences();
  }, []);

  // Fetch user's quick actions preferences
  const fetchQuickActionsPreferences = async () => {
    try {
      const response = await axios.get('/api/user/quick-actions-preferences', {
        withCredentials: true
      });

      if (response.data.preferences && response.data.preferences.length > 0) {
        setSelectedQuickActions(response.data.preferences);
      } else {
        // Default to first 5 actions if no preferences saved
        const allActions = getQuickActions();
        setSelectedQuickActions(allActions.slice(0, 5).map(a => a.id));
      }
    } catch (error) {
      console.error('Error fetching quick actions preferences:', error);
      // Default to first 5 actions on error
      const allActions = getQuickActions();
      setSelectedQuickActions(allActions.slice(0, 5).map(a => a.id));
    }
  };

  const handleQuickActionsSave = (preferences) => {
    setSelectedQuickActions(preferences);
  };

  // Fetch Content Draft items
  const fetchContentDraftItems = async () => {
    try {
      const response = await axios.get('/api/content-drafts', { withCredentials: true });
      const all = response.data.drafts || [];
      const isOperator = user?.is_operator || user?.role === 'super_admin';
      const agentStatuses = ['pending_operator_review', 'revision_requested', 'denied', 'approved'];
      const filtered = isOperator
        ? all.filter(d => agentStatuses.includes(d.status))
        : all.filter(d => d.status === 'sent_to_client');

      // Ensure brand_id and organization_id are included for navigation
      const enriched = filtered.map(draft => ({
        ...draft,
        brandId: draft.brand?._id || draft.brand?.id,
        organizationId: draft.organization?._id || draft.organization?.id
      }));

      setContentDraftItems(enriched);
    } catch (error) {
      console.error('Error fetching content drafts:', error);
    }
  };

  // Fetch Ideas items
  const fetchIdeasItems = async () => {
    try {
      const response = await axios.get('/api/ideas', { withCredentials: true });
      const allIdeas = response.data.ideas || [];
      const isOperator = user?.is_operator || user?.role === 'super_admin';
      const visibleStatuses = isOperator ? ['revision_requested', 'denied', 'pending_review'] : ['sent_to_client'];
      const flattenedIdeas = [];
      allIdeas.forEach(doc => {
        (doc.ideas || []).forEach(idea => {
          if (visibleStatuses.includes(idea.status)) {
            flattenedIdeas.push({
              ...idea,
              docId: doc._id,
              brandName: doc.brand?.name || 'Unknown Brand',
              brandLogo: doc.brand?.icon_url,
              brandId: doc.brand?._id || doc.brand?.id,
              organizationName: doc.organization?.name || 'Unknown Organization',
              organizationId: doc.organization?._id || doc.organization?.id,
              generatedAt: doc.generated_at
            });
          }
        });
      });
      setIdeasItems(flattenedIdeas);
    } catch (error) {
      console.error('Error fetching ideas:', error);
    }
  };

  // Fetch Brand Voice completions
  const fetchBrandVoiceCompletions = async () => {
    try {
      const isOperator = user?.is_operator || user?.role === 'super_admin';
      if (!isOperator) {
        // Only operators see brand voice completions
        setBrandVoiceItems([]);
        return;
      }

      const response = await axios.get('/api/brand-voice/completions', { withCredentials: true });
      const completions = response.data.completions || [];
      setBrandVoiceItems(completions);
    } catch (error) {
      console.error('Error fetching brand voice completions:', error);
      setBrandVoiceItems([]);
    }
  };

  const handleItemNavigation = async (item, targetSection) => {
    // Extract brand ID and org ID from the item
    let brandId = item.brandId || item.brand_id;
    let orgId = item.organizationId || item.organization_id;

    // Find the brand and org in the context arrays
    const brand = brands.find(b => (b._id || b.id) === brandId);
    const org = organizations.find(o => (o._id || o.id) === orgId);

    let contextUpdated = false;

    // Switch organization first if needed
    if (org) {
      const currentOrgId = selectedOrganization?._id || selectedOrganization?.id;
      if (currentOrgId !== orgId) {
        await setOrganization(org);
        contextUpdated = true;
      }
    }

    // Then switch brand if needed
    if (brand) {
      const currentBrandId = selectedBrand?._id || selectedBrand?.id;
      if (currentBrandId !== brandId) {
        await setBrand(brand);
        contextUpdated = true;
      }
    }

    // Special handling for brand voice items
    if (targetSection === 'brand-voice') {
      sessionStorage.setItem('open_brand_voice_tab', 'true');
      sessionStorage.setItem('pending_brand_voice_id', brandId);
    }

    // Force the correct tab when navigating to review panels
    if (targetSection === 'operator-review-ideas' || targetSection === 'client-review-ideas') {
      sessionStorage.setItem('force_ideas_panel_tab', 'current');
    } else if (targetSection === 'review-content') {
      sessionStorage.setItem('force_review_content_tab', 'current');
    } else if (targetSection === 'schedule-posts') {
      localStorage.setItem('schedule_posts_active_tab', 'ready');
    }

    // Show success notification if context was updated
    if (contextUpdated && brand && org) {
      modal.toast(`Context updated: ${org.name || org.organization_name} > ${brand.name || brand.brand_name}`, 'success');
    } else if (contextUpdated && brand) {
      modal.toast(`Switched to ${brand.name || brand.brand_name}`, 'success');
    }

    // Navigate to target section
    if (onSectionChange && targetSection) {
      onSectionChange(targetSection === 'brand-voice' ? 'assets' : targetSection);
    }
  };


  // Fetch Calendar events for the current two weeks
  const fetchCalendarEvents = async () => {
    try {
      setCalendarLoading(true);
      const now = new Date();
      // Get start of current week (Sunday)
      const weekStart = new Date(now);
      weekStart.setDate(weekStart.getDate() - weekStart.getDay());
      weekStart.setHours(0, 0, 0, 0);
      // Get end of next week (Saturday)
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 13);
      weekEnd.setHours(23, 59, 59, 999);

      const params = new URLSearchParams({
        timeMin: weekStart.toISOString(),
        timeMax: weekEnd.toISOString(),
        maxResults: 100
      });

      const response = await fetch(`/api/calendar/events?${params}`, {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });

      const data = await response.json();

      if (response.ok && data.events) {
        // Extract dates with events, filtering out system-generated events
        const eventDates = data.events
          .filter(event => event.eventType !== 'workingLocation' && event.eventType !== 'outOfOffice')
          .filter(event => event.start && (event.start.dateTime || event.start.date))
          .map(event => {
            const dateStr = event.start.dateTime || event.start.date;
            return new Date(dateStr).toDateString();
          });
        setCalendarEvents([...new Set(eventDates)]); // Unique dates
        setCalendarError(null);
      } else if (data.no_calendar_access) {
        // User doesn't have Google Calendar access
        setCalendarError('no_access');
      }
    } catch (error) {
      console.error('Error fetching calendar events:', error);
      setCalendarError('error');
    } finally {
      setCalendarLoading(false);
    }
  };

  // Handle idea decisions
  const handleIdeaDecision = async (docId, ideaId, decision, comment = null) => {
    // Optimistically remove the item immediately so the UI responds instantly
    setIdeasItems(prev => prev.filter(i => !(i.docId === docId && i.id === ideaId)));
    try {
      await axios.put(`/api/ideas/${docId}/idea/${ideaId}/status`, {
        status: decision,
        comment: comment || undefined
      }, { withCredentials: true });
    } catch (error) {
      console.error('Error updating idea decision:', error);
      modal.error(error.response?.data?.error || 'Failed to update idea');
      fetchIdeasItems(); // Restore state on error
    }
  };

  // Handle revision modal submission
  const handleRevisionSubmit = async () => {
    if (!revisionComment.trim()) {
      modal.warning('Please enter a comment explaining what needs revision');
      return;
    }

    if (revisionModal.type === 'idea') {
      await handleIdeaDecision(revisionModal.docId, revisionModal.ideaId, 'revision_requested', revisionComment);
    }

    // Close modal and reset
    setRevisionModal({ open: false, type: null, itemId: null, docId: null, ideaId: null });
    setRevisionComment('');
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatCommentTimestamp = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const month = date.getMonth() + 1;
    const day = date.getDate();
    let hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12;
    const minutesStr = minutes < 10 ? '0' + minutes : minutes;
    return `${month}/${day} ${hours}:${minutesStr} ${ampm}`;
  };

  const getBrandDisplay = (item) => {
    const brandName = item.brand_name || item.brandName || 'Unknown';
    if (item.brand_logo) {
      return <BrandIcon src={item.brand_logo} alt={brandName} className="mini-brand-logo" />;
    }
    return (
      <div className="mini-brand-placeholder">
        <span>{brandName.charAt(0).toUpperCase()}</span>
      </div>
    );
  };

  // Combine ideas, content drafts, and brand voice for the mini list
  const allItems = [
    ...ideasItems.map(idea => {
      const isOperator = user?.is_operator || user?.role === 'super_admin';
      return {
        id: `${idea.docId}-${idea.id}`,
        type: 'idea',
        ideaStatus: idea.status,
        brand: idea.brandName,
        brandLogo: idea.brandLogo,
        brandId: idea.brandId,
        organizationId: idea.organizationId,
        content: idea.title,
        date: idea.sent_to_client_at || idea.generatedAt,
        comments: idea.comments || [],
        docId: idea.docId,
        ideaId: idea.id,
        navigateTo: isOperator ? 'operator-review-ideas' : 'client-review-ideas'
      };
    }),
    ...contentDraftItems.map(draft => {
      const isOperator = user?.is_operator || user?.role === 'super_admin';
      return {
        id: draft._id,
        type: 'content_draft',
        contentDraftStatus: draft.status,
        brand: draft.brand_name || draft.brand?.name || 'Unknown',
        brandLogo: draft.brand?.icon_url,
        brandId: draft.brandId,
        organizationId: draft.organizationId,
        // Operators track by the internal record title; clients see the actual content headline
        content: (isOperator ? (draft.title || draft.headline) : (draft.headline || draft.title)) || 'Untitled Content',
        date: draft.sent_to_client_at || draft.created_at,
        comments: draft.comments || [],
        navigateTo: draft.status === 'approved' ? 'schedule-posts' : 'review-content'
      };
    }),
    ...brandVoiceItems.map(completion => ({
      id: completion._id,
      type: 'brand_voice',
      brand: completion.brand_name || 'Unknown',
      brandLogo: completion.brand_logo,
      brandId: completion.brand_id,
      organizationId: completion.organization_id,
      content: `${completion.brand_name || 'Brand'} - Brand Voice Designer`,
      date: completion.completed_at,
      comments: completion.comments || [],
      brandVoiceData: completion,
      navigateTo: 'brand-voice'
    }))
  ].sort((a, b) => {
    // Default sort: newest first (by date descending)
    const dateA = new Date(a.date || 0);
    const dateB = new Date(b.date || 0);
    return dateB - dateA;
  }); // Show all items with scrolling

  // Calendar helpers
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const getTwoWeeks = (startDate) => {
    const days = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Get the start of the current week (Sunday)
    const currentWeekStart = new Date(startDate);
    currentWeekStart.setDate(currentWeekStart.getDate() - currentWeekStart.getDay());
    currentWeekStart.setHours(0, 0, 0, 0);

    // Show 14 days (current week + next week)
    for (let i = 0; i < 14; i++) {
      const dayDate = new Date(currentWeekStart);
      dayDate.setDate(currentWeekStart.getDate() + i);

      days.push({
        day: dayDate.getDate(),
        isCurrentMonth: true,
        isToday: dayDate.toDateString() === today.toDateString(),
        hasEvent: calendarEvents.includes(dayDate.toDateString()),
        fullDate: dayDate
      });
    }

    return days;
  };

  const navigateWeek = (direction) => {
    const newDate = new Date(currentMonth);
    newDate.setDate(newDate.getDate() + (direction * 7)); // Move by 7 days
    setCurrentMonth(newDate);

    // Fetch events for the new two-week period
    const weekStart = new Date(newDate);
    weekStart.setDate(weekStart.getDate() - weekStart.getDay());
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 13); // 14 days total

    fetch(`/api/calendar/events?timeMin=${weekStart.toISOString()}&timeMax=${weekEnd.toISOString()}&maxResults=100`, {
      credentials: 'include'
    })
      .then(res => res.json())
      .then(data => {
        if (data.events) {
          const eventDates = data.events
            .filter(event => event.eventType !== 'workingLocation' && event.eventType !== 'outOfOffice')
            .filter(event => event.start && (event.start.dateTime || event.start.date))
            .map(event => {
              const dateStr = event.start.dateTime || event.start.date;
              return new Date(dateStr).toDateString();
            });
          setCalendarEvents([...new Set(eventDates)]);
          setCalendarError(null);
        } else if (data.no_calendar_access) {
          setCalendarError('no_access');
        }
      })
      .catch(err => {
        console.error('Error fetching events:', err);
        setCalendarError('error');
      });
  };

  const getWeekRangeLabel = (date) => {
    const weekStart = new Date(date);
    weekStart.setDate(weekStart.getDate() - weekStart.getDay());
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 13);

    const startMonth = monthNames[weekStart.getMonth()];
    const endMonth = monthNames[weekEnd.getMonth()];
    const startDay = weekStart.getDate();
    const endDay = weekEnd.getDate();

    if (startMonth === endMonth) {
      return `${startMonth} ${startDay}-${endDay}`;
    } else {
      return `${startMonth} ${startDay} - ${endMonth} ${endDay}`;
    }
  };

  const totalPendingItems = ideasItems.length + contentDraftItems.length + brandVoiceItems.length;

  // Get quick actions based on user role (icons match LeftNav parent menu)
  const getQuickActions = () => {
    switch (userRole) {
      case USER_ROLES.SUPER_ADMIN:
        return [
          { id: 'activity-logs', label: 'Activity Logs', icon: 'superadmin', section: 'activity-log' },
          { id: 'create-agency', label: 'Create Agency', icon: 'superadmin', section: 'create-agency' },
          { id: 'organizations', label: 'Organizations & Brands', icon: 'building', section: 'admin-organizations-brands' },
          { id: 'proxy-user', label: 'Proxy User', icon: 'superadmin', section: 'proxy-user' },
          { id: 'user-management', label: 'User Management', icon: 'superadmin', section: 'admin-user-management' }
        ];
      case USER_ROLES.ADMIN:
        return [
          { id: 'activity-logs', label: 'Activity Logs', icon: 'admin', section: 'activity-log' },
          { id: 'create-content', label: 'Create Content', icon: 'pencil', section: 'create' },
          { id: 'organizations', label: 'Organizations & Brands', icon: 'building', section: 'admin-organizations-brands' },
          { id: 'proxy-user', label: 'Proxy User', icon: 'admin', section: 'proxy-user' },
          { id: 'user-management', label: 'User Management', icon: 'admin', section: 'admin-user-management' }
        ];
      case USER_ROLES.OPERATOR:
        return [
          { id: 'brand-identity', label: 'Brand Identity', icon: 'vkontakte-rect', section: 'assets' },
          { id: 'create-content', label: 'Create Content', icon: 'pencil', section: 'create' },
          { id: 'recordings', label: 'Recordings', icon: 'lightbulb', section: 'recordings' },
          { id: 'review-content', label: 'Review Content', icon: 'pencil', section: 'review-content' },
          { id: 'schedule-posts', label: 'Schedule Posts', icon: 'pencil', section: 'schedule-posts' }
        ];
      case USER_ROLES.CLIENT:
        return [
          { id: 'brand-voice', label: 'Brand Voice Designer', icon: 'vkontakte-rect', section: 'brand-builder' },
          { id: 'calendar', label: 'Meeting Calendar', icon: 'calendar', section: 'calendar' },
          { id: 'review-content', label: 'Review Content', icon: 'pencil', section: 'review-content' },
          { id: 'client-review-ideas', label: 'Review Ideas', icon: 'lightbulb', section: 'client-review-ideas' }
        ];
      default:
        return [];
    }
  };

  const allQuickActions = getQuickActions();
  const quickActions = selectedQuickActions.length > 0
    ? allQuickActions.filter(action => selectedQuickActions.includes(action.id))
    : allQuickActions.slice(0, 5);

  return (
    <div className="dashboard-home">
      {/* TEMPORARY: Modal System Test Panel - Only visible to ben@blkrvn.co */}
      {/* Commented out after testing - uncomment to test modal system
      {user?.email === 'ben@blkrvn.co' && (
        <div style={{
          backgroundColor: 'var(--color-surface-brand-bar)',
          borderRadius: '12px',
          padding: '1.5rem',
          margin: '1rem',
          border: '2px solid var(--color-brand-blue)'
        }}>
        <h3 style={{ color: 'var(--color-text-primary)', marginTop: 0, marginBottom: '1rem' }}>
          🎨 Modal System Test Panel (Remove after testing)
        </h3>
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
          <button
            onClick={() => modal.success('Content created successfully!')}
            style={{
              padding: '0.625rem 1.25rem',
              backgroundColor: 'var(--color-success)',
              color: 'var(--color-text-on-accent)',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: '500'
            }}
          >
            Test Success Toast
          </button>
          <button
            onClick={() => modal.error('Failed to save content')}
            style={{
              padding: '0.625rem 1.25rem',
              backgroundColor: 'var(--color-error)',
              color: 'var(--color-text-on-accent)',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: '500'
            }}
          >
            Test Error Toast
          </button>
          <button
            onClick={() => modal.warning('You have unsaved changes')}
            style={{
              padding: '0.625rem 1.25rem',
              backgroundColor: 'var(--color-warning)',
              color: 'var(--color-text-on-accent)',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: '500'
            }}
          >
            Test Warning Toast
          </button>
          <button
            onClick={() => modal.info('Processing your request...')}
            style={{
              padding: '0.625rem 1.25rem',
              backgroundColor: 'var(--color-brand-blue)',
              color: 'var(--color-text-on-accent)',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: '500'
            }}
          >
            Test Info Toast
          </button>
          <button
            onClick={async () => {
              const result = await modal.confirm(
                'Are you sure you want to delete this item? This action cannot be undone.',
                'Confirm Delete'
              );
              if (result) {
                modal.success('Item deleted!');
              } else {
                modal.info('Deletion cancelled');
              }
            }}
            style={{
              padding: '0.625rem 1.25rem',
              backgroundColor: 'var(--color-neutral)',
              color: 'var(--color-text-on-accent)',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: '500'
            }}
          >
            Test Confirm Dialog
          </button>
          <button
            onClick={() => modal.info('Please select a brand first')}
            style={{
              padding: '0.625rem 1.25rem',
              backgroundColor: 'var(--color-brand-purple)',
              color: 'var(--color-text-on-accent)',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: '500'
            }}
          >
            Test Info Toast (was Alert Dialog)
          </button>
        </div>
      </div>
      )}
      */}

      <div className="dashboard-widgets">
        {/* Mini To Do List Widget */}
        <div className="dashboard-widget todo-widget">
          <div className="widget-header">
            <div className="widget-title">
              <Icon name="check" size="1.25rem" />
              <h3>To Do List</h3>
              {totalPendingItems > 0 && (
                <span className="widget-badge">{totalPendingItems}</span>
              )}
            </div>
            <button
              className="widget-goto-btn"
              onClick={() => onSectionChange('todo')}
            >
              View All
              <Icon name="chevron-right" size="1rem" />
            </button>
          </div>

          <div className="widget-content">
            {todoLoading ? (
              <div className="widget-loading">
                <div className="mini-spinner"></div>
              </div>
            ) : allItems.length === 0 ? (
              <div className="widget-empty">
                <Icon name="check" size="2rem" />
                <p>No pending items</p>
              </div>
            ) : (
              <table className="mini-todo-table">
                <thead>
                  <tr>
                    <th>Brand</th>
                    <th>Ideas/Content</th>
                    <th>Received</th>
                    <th>Actions</th>
                    <th>Comment</th>
                  </tr>
                </thead>
                <tbody>
                  {allItems.map((item) => (
                    <tr key={item.id}>
                      <td className="mini-brand-cell">
                        <div className="mini-brand-display">
                          {item.brandLogo ? (
                            <BrandIcon src={item.brandLogo} alt={item.brand} className="mini-brand-logo" />
                          ) : (
                            <div className="mini-brand-placeholder">
                              <span>{item.brand.charAt(0).toUpperCase()}</span>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="mini-content-cell">
                        <span className="mini-content-text">{item.content}</span>
                        {item.subContent && (
                          <span className="mini-sub-content">{item.subContent}</span>
                        )}
                        <div className="mini-badge-row">
                          <span className={`mini-type-badge ${item.type}`}>
                            {item.type === 'idea' ? 'Idea' :
                             item.type === 'content_draft' ? 'Content' :
                             'Brand Voice'}
                          </span>
                          {item.type === 'content_draft' && item.contentDraftStatus === 'approved' && (
                            <span className="mini-status-badge approved">Ready To Schedule</span>
                          )}
                          {item.ideaStatus === 'revision_requested' && (
                            <span className="mini-status-badge revision">Revision</span>
                          )}
                          {item.ideaStatus === 'denied' && (
                            <span className="mini-status-badge denied">Denied</span>
                          )}
                        </div>
                      </td>
                      <td className="mini-date-cell">
                        {formatDate(item.date)}
                      </td>
                      <td className="mini-approve-cell">
                        {item.navigateTo || item.onReview ? (
                          <button
                            className="review-nav-btn"
                            onClick={() => item.onReview ? item.onReview() : handleItemNavigation(item, item.navigateTo)}
                            title={
                              item.type === 'content_draft' && item.contentDraftStatus === 'approved' ? 'View in Schedule Posts' :
                              item.type === 'content_draft' ? 'View in Review Content' :
                              item.type === 'brand_voice' ? 'Review Brand Voice' :
                              'View in Review Ideas'
                            }
                          >
                            <Icon name="go-to-link" size="1rem" />
                            {item.type === 'content_draft' && item.contentDraftStatus === 'approved' ? 'Schedule' : 'Review'}
                          </button>
                        ) : (
                          <div className="approve-buttons">
                            <button
                              className="approve-btn approve"
                              onClick={item.onApprove}
                              title="Approve"
                            >
                              <Icon name="ok" size="1.25rem" />
                            </button>
                            <button
                              className="approve-btn flag"
                              onClick={item.onRevise}
                              title="Flag for revision"
                            >
                              <Icon name="flag" size="1.25rem" />
                            </button>
                            <button
                              className="approve-btn deny"
                              onClick={item.onDeny}
                              title="Deny"
                            >
                              <Icon name="cancel" size="1.25rem" />
                            </button>
                          </div>
                        )}
                      </td>
                      <td className="mini-comment-cell">
                        <button
                          className="mini-comment-icon-btn"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            const itemType = item.type === 'idea' ? 'idea' :
                                           item.type === 'content_draft' ? 'content-draft' :
                                           'brand-voice-completion';
                            setCommentModalState({
                              open: true,
                              itemId: item.type === 'idea' ? item.ideaId : item.id,
                              itemType: itemType,
                              docId: item.type === 'idea' ? item.docId : null,
                              comments: item.comments || []
                            });
                          }}
                          title="Add comment"
                          type="button"
                        >
                          <Icon name="comment-empty" size="1.5rem" />
                          {((item.type === 'idea' && item.comments && item.comments.length > 0) ||
                            (item.type === 'content_draft' && item.comments && item.comments.length > 0) ||
                            (item.type === 'brand_voice' && item.comments && item.comments.length > 0)) && (
                            <span className="mini-comment-count-badge">
                              {item.comments.length}
                            </span>
                          )}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Mini Calendar Widget */}
        <div className="dashboard-widget calendar-widget">
          <div className="widget-header">
            <div className="widget-title">
              <Icon name="calendar" size="1.25rem" />
              <h3>Calendar</h3>
            </div>
            <button
              className="widget-goto-btn"
              onClick={() => {
                if (isProxyMode) {
                  onSectionChange('calendar');
                } else {
                  setSearchParams({ section: 'calendar', subsection: 'calendar-view' });
                }
              }}
            >
              View Full
              <Icon name="chevron-right" size="1rem" />
            </button>
          </div>

          <div className="widget-content">
            {calendarLoading ? (
              <div className="widget-loading">
                <div className="mini-spinner"></div>
              </div>
            ) : calendarError === 'no_access' ? (
              <div className="widget-empty">
                <Icon name="calendar" size="2rem" />
                <p>No Access to Google Calendar</p>
                <small style={{ color: 'var(--color-text-tertiary)', fontSize: '0.85rem', marginTop: '0.5rem' }}>
                  Your account doesn't have Calendar enabled
                </small>
              </div>
            ) : (
              <div className="mini-calendar">
                <div className="mini-calendar-header">
                  <button
                    className="month-nav-btn"
                    onClick={() => navigateWeek(-1)}
                  >
                    <Icon name="chevron-left" size="1rem" />
                  </button>
                  <span className="month-year">
                    {getWeekRangeLabel(currentMonth)}
                  </span>
                  <button
                    className="month-nav-btn"
                    onClick={() => navigateWeek(1)}
                  >
                    <Icon name="chevron-right" size="1rem" />
                  </button>
                </div>

                <div className="mini-calendar-grid">
                  <div className="mini-calendar-days-header">
                    {dayNames.map(day => (
                      <div key={day} className="mini-day-name">{day}</div>
                    ))}
                  </div>
                  <div className="mini-calendar-days">
                    {getTwoWeeks(currentMonth).map((dayInfo, index) => (
                      <div
                        key={index}
                        className={`mini-day ${dayInfo.isCurrentMonth ? '' : 'other-month'} ${dayInfo.isToday ? 'today' : ''} ${dayInfo.hasEvent ? 'has-event' : ''}`}
                        onClick={() => dayInfo.hasEvent && onSectionChange('calendar')}
                      >
                        {dayInfo.day}
                        {dayInfo.hasEvent && <div className="event-dot"></div>}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="calendar-legend">
                  <div className="legend-item">
                    <div className="event-dot"></div>
                    <span>Meeting scheduled</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Quick Actions Widget */}
        <div className="dashboard-widget quick-actions-widget">
          <div className="widget-header">
            <div className="widget-title">
              <Icon name="go-to-link" size="1.25rem" />
              <h3>Quick Actions</h3>
            </div>
            <button
              className="widget-goto-btn"
              onClick={() => setShowQuickActionsModal(true)}
            >
              Edit
              <Icon name="chevron-right" size="1rem" />
            </button>
          </div>

          <div className="widget-content">
            <div className="quick-actions-list">
              {quickActions.map((action) => (
                <button
                  key={action.id}
                  className="quick-action-item"
                  onClick={() => onSectionChange(action.section)}
                >
                  <div className="quick-action-icon">
                    <Icon name={action.icon} size="1.25rem" />
                  </div>
                  <span className="quick-action-label">{action.label}</span>
                  <div className="quick-action-arrow">
                    <Icon name="chevron-right" size="0.875rem" />
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Revision Comment Modal */}
      {revisionModal.open && (
        <div className="revision-modal-overlay" onClick={() => setRevisionModal({ open: false, type: null, itemId: null, docId: null, ideaId: null })}>
          <div className="revision-modal" onClick={(e) => e.stopPropagation()}>
            <div className="revision-modal-header">
              <h3>Request Revision</h3>
              <button
                className="modal-close-btn"
                onClick={() => setRevisionModal({ open: false, type: null, itemId: null, docId: null, ideaId: null })}
                aria-label="Close"
              />
            </div>
            <div className="revision-modal-content">
              <p className="revision-modal-description">
                Please explain what needs to be revised so the operator can make the necessary changes.
              </p>
              <div className="revision-comment-user-info">
                <Avatar user={user} size="sm" className="revision-profile-pic" />
                <span className="revision-user-name">{user.name || 'User'}</span>
              </div>
              <textarea
                value={revisionComment}
                onChange={(e) => setRevisionComment(e.target.value)}
                placeholder="Enter your feedback or what needs to be revised..."
                rows={5}
                autoFocus
              />
              <div className="revision-modal-actions">
                <button
                  className="btn-cancel"
                  onClick={() => {
                    setRevisionModal({ open: false, type: null, itemId: null, docId: null, ideaId: null });
                    setRevisionComment('');
                  }}
                >
                  Cancel
                </button>
                <button
                  className={`btn-submit ${!revisionComment.trim() ? 'disabled' : ''}`}
                  onClick={handleRevisionSubmit}
                  disabled={!revisionComment.trim()}
                >
                  Send Revision Request
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Comment Modal */}
      <CommentModal
        isOpen={commentModalState.open}
        onClose={() => setCommentModalState({ open: false, itemId: null, itemType: null, docId: null, comments: [] })}
        itemId={commentModalState.itemId}
        itemType={commentModalState.itemType}
        docId={commentModalState.docId}
        comments={commentModalState.comments}
        user={user}
        onCommentAdded={(updatedComments) => {
          if (commentModalState.itemType === 'idea') {
            setIdeasItems(prev => prev.map(item =>
              item.id === commentModalState.itemId ? { ...item, comments: updatedComments } : item
            ));
          } else if (commentModalState.itemType === 'content-draft') {
            setContentDraftItems(prev => prev.map(item =>
              item._id === commentModalState.itemId ? { ...item, comments: updatedComments } : item
            ));
          } else if (commentModalState.itemType === 'brand-voice-completion') {
            setBrandVoiceItems(prev => prev.map(item =>
              item._id === commentModalState.itemId ? { ...item, comments: updatedComments } : item
            ));
          }
        }}
        onCommentDeleted={(updatedComments) => {
          if (commentModalState.itemType === 'idea') {
            setIdeasItems(prev => prev.map(item =>
              item.id === commentModalState.itemId ? { ...item, comments: updatedComments } : item
            ));
          } else if (commentModalState.itemType === 'content-draft') {
            setContentDraftItems(prev => prev.map(item =>
              item._id === commentModalState.itemId ? { ...item, comments: updatedComments } : item
            ));
          } else if (commentModalState.itemType === 'brand-voice-completion') {
            setBrandVoiceItems(prev => prev.map(item =>
              item._id === commentModalState.itemId ? { ...item, comments: updatedComments } : item
            ));
          }
        }}
        isProxyMode={isProxyMode}
      />

      {/* Quick Actions Edit Modal */}
      <QuickActionsEditModal
        isOpen={showQuickActionsModal}
        onClose={() => setShowQuickActionsModal(false)}
        onSave={handleQuickActionsSave}
        user={user}
      />
    </div>
  );
};

export default DashboardHome;
